import { FunctionComponent, useEffect, useState } from 'react'
import { TableCell, TableRow } from '@mui/material'
import { StaffWithLinks } from '@/types/Staff.ts'
import { CalendarEventType, EventCalendar } from '@/types/Calendar.ts'
import styles from './staffWorkTableRow.module.scss'
import { useTranslation } from 'react-i18next'
import { Daycare } from '@/types/Daycare.ts'
import { Person } from '@mui/icons-material'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon.tsx'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { Work } from '@/types/work.ts'
import { getDaycareClosing, getDaycareOpening } from '@/modules/helper/daycareHelper.ts'
import Desktop from '@/modules/desktopMobile/desktop.tsx'
import Mobile from '@/modules/desktopMobile/mobile.tsx'
import StaffWorkDayCell from './StaffWorkDayCell.tsx'
import {
    filterEventsForWeek,
    formatHoursMinutes,
    getHoursAndMinutesFromWorks,
    sortEventsByDate
} from '../../utils/calendarHelpers.ts'
import { Moment } from 'moment-timezone'

interface StaffWorkTableRowProps {
    staff: StaffWithLinks
    calendarEvents: EventCalendar[]
    columnWidth: string
    startOfWeek: Moment
    currentDaycare: Daycare
    staffsListData: Array<StaffWithLinks>
    refetchData: () => void
    isMobile: boolean
}

interface WeekDay {
    events: EventCalendar[]
    isOpen: boolean
    dayIndex: number
}

const StaffWorkTableRow: FunctionComponent<StaffWorkTableRowProps> = ({
    staff,
    calendarEvents,
    columnWidth,
    startOfWeek,
    currentDaycare,
    staffsListData,
    isMobile,
    refetchData
}) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [staffEvents, setStaffEvents] = useState<EventCalendar[]>([])
    const [weeklyEvents, setWeeklyEvents] = useState<{
        monday: EventCalendar[]
        tuesday: EventCalendar[]
        wednesday: EventCalendar[]
        thursday: EventCalendar[]
        friday: EventCalendar[]
        saturday: EventCalendar[]
        sunday: EventCalendar[]
    }>({
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
    })

    const weekDays: WeekDay[] = [
        { events: weeklyEvents.monday, isOpen: !!currentDaycare.opening.monOpen, dayIndex: 0 },
        { events: weeklyEvents.tuesday, isOpen: !!currentDaycare.opening.tueOpen, dayIndex: 1 },
        { events: weeklyEvents.wednesday, isOpen: !!currentDaycare.opening.wedOpen, dayIndex: 2 },
        { events: weeklyEvents.thursday, isOpen: !!currentDaycare.opening.thuOpen, dayIndex: 3 },
        { events: weeklyEvents.friday, isOpen: !!currentDaycare.opening.friOpen, dayIndex: 4 },
        { events: weeklyEvents.saturday, isOpen: !!currentDaycare.opening.satOpen, dayIndex: 5 },
        { events: weeklyEvents.sunday, isOpen: !!currentDaycare.opening.sunOpen, dayIndex: 6 }
    ]

    const [works, setWorks] = useState<Work[]>([])

    const [staffHours, setStaffHours] = useState<number>(0)
    const [staffMinutes, setStaffMinutes] = useState<number>(0)

    useEffect(() => {
        const filteredStaffEvents: EventCalendar[] = sortEventsByDate(
            calendarEvents.filter((event: EventCalendar) => event.staffId === staff.id)
        )

        setStaffEvents(filteredStaffEvents)
        setWeeklyEvents(filterEventsForWeek(filteredStaffEvents))

        const works: Work[] = filteredStaffEvents
            .filter(
                (event: EventCalendar) =>
                    event.type === CalendarEventType.STAFF_TRAINING ||
                    event.type === CalendarEventType.STAFF_HOLIDAY ||
                    event.type === CalendarEventType.NOT_IN_SECTION ||
                    event.type === CalendarEventType.IN_SECTION
            )
            .map((event: EventCalendar) => event.slots as Work)

        setWorks(works)

        const { hours, minutes } = getHoursAndMinutesFromWorks(works)
        setStaffHours(hours)
        setStaffMinutes(minutes)
    }, [calendarEvents])

    const canRead: boolean = user?.currentDaycareRights.canReadStaffWorks === true
    const canWrite: boolean = user?.currentDaycareRights.canWriteStaffWorks === true

    const self: boolean = staff.email === user?.email

    return canRead || (!canRead && self) ? (
        <TableRow className={self ? styles.RowBackgroundForMe : ''}>
            <TableCell sx={{ width: isMobile ? '50%' : '30%' }}>
                <div className={styles.StaffCell}>
                    <ButtonIcon
                        icon={<Person />}
                        className={styles.ButtonIcon}
                        onClick={() =>
                            self
                                ? navigate('/account/profile')
                                : user?.currentDaycareRights.canReadStaffRights &&
                                  navigate(`/staff?profile=${staff.id}`)
                        }
                    />
                    <div className={styles.Info}>
                        <span className={styles.Name}>{self ? t('me') : `${staff.firstname} ${staff.lastname}`}</span>
                        <span className={styles.Hours}>{formatHoursMinutes(staffHours, staffMinutes)}</span>
                    </div>
                </div>
            </TableCell>
            <Desktop>
                {weekDays.map((day) => (
                    <StaffWorkDayCell
                        key={day.dayIndex}
                        events={day.events}
                        isOpen={day.isOpen}
                        columnWidth={columnWidth}
                        canWrite={canWrite}
                        date={startOfWeek.clone().add(day.dayIndex, 'd').format('DD-MM-YYYY')}
                        works={works}
                        from={getDaycareOpening(currentDaycare, day.dayIndex) || '08:00'}
                        to={getDaycareClosing(currentDaycare, day.dayIndex) || '17:00'}
                        staffsListData={staffsListData}
                        daycare={currentDaycare}
                        staffId={staff.id.toString()}
                        refetchData={refetchData}
                    />
                ))}
            </Desktop>
            <Mobile>
                <StaffWorkDayCell
                    events={staffEvents}
                    isOpen={true}
                    columnWidth={'50%'}
                    canWrite={canWrite}
                    date={startOfWeek.clone().format('DD-MM-YYYY')}
                    works={works}
                    from={getDaycareOpening(currentDaycare, (startOfWeek.day() + 6) % 7) || '08:00'}
                    to={getDaycareClosing(currentDaycare, (startOfWeek.day() + 6) % 7) || '17:00'}
                    staffsListData={staffsListData}
                    daycare={currentDaycare}
                    staffId={staff.id.toString()}
                    refetchData={refetchData}
                />
            </Mobile>
        </TableRow>
    ) : null
}

export default StaffWorkTableRow
